import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createApi, type ZwApi } from './api/client';
import {
  formatLabel,
  resolveLabels,
  type LabelKey,
  type LabelVars,
  type Labels,
} from './i18n/labels';

export interface ZeitwahlOptions {
  apiBaseUrl: string;
  widgetKey?: string;
  fetch?: typeof fetch;
  headers?: Record<string, string>;
}

export interface ZeitwahlProviderProps {
  /** `''` on hosted pages, `/api/booking` behind a site proxy, or the Zeitwahl origin. */
  apiBaseUrl?: string;
  widgetKey?: string;
  /** BCP 47 tag; `de*` selects the German labels, everything else English. Also drives `Intl`. */
  locale?: string;
  labels?: Partial<Labels>;
  /** Initial IANA zone for the visitor; defaults to the browser's zone at first render. */
  timezone?: string;
  fetch?: typeof fetch;
  headers?: Record<string, string>;
  children?: ReactNode;
}

export interface ZeitwahlContextValue {
  api: ZwApi;
  options: ZeitwahlOptions;
  locale: string;
  labels: Labels;
  labelOverrides?: Partial<Labels>;
  timeZone?: string;
  t: (key: LabelKey, vars?: LabelVars) => string;
}

const ZeitwahlContext = createContext<ZeitwahlContextValue | null>(null);

const parseHeaders = (key: string): Record<string, string> | undefined =>
  key ? (JSON.parse(key) as Record<string, string>) : undefined;

/**
 * Supplies the API client, locale, labels and default zone to every hook and component below it.
 * Providers nest: an inner one inherits whatever the outer one set and overrides only its own props.
 */
export function ZeitwahlProvider(props: ZeitwahlProviderProps): ReactNode {
  const parent = useContext(ZeitwahlContext);
  const apiBaseUrl = props.apiBaseUrl ?? parent?.options.apiBaseUrl ?? '';
  const widgetKey = props.widgetKey ?? parent?.options.widgetKey;
  const fetchImpl = props.fetch ?? parent?.options.fetch;
  const headers = props.headers ?? parent?.options.headers;
  const headersKey = headers ? JSON.stringify(headers) : '';
  const locale = props.locale ?? parent?.locale ?? 'en';
  const timeZone = props.timezone ?? parent?.timeZone;
  const parentOverrides = parent?.labelOverrides;
  const ownOverrides = props.labels;

  // `headers` is compared by content (headersKey), so inline objects do not recreate the client.
  const api = useMemo(
    () => createApi({ apiBaseUrl, widgetKey, fetch: fetchImpl, headers: parseHeaders(headersKey) }),
    [apiBaseUrl, widgetKey, fetchImpl, headersKey]
  );

  const value = useMemo<ZeitwahlContextValue>(() => {
    const labelOverrides =
      parentOverrides || ownOverrides ? { ...parentOverrides, ...ownOverrides } : undefined;
    const labels = resolveLabels(locale, labelOverrides);
    return {
      api,
      options: { apiBaseUrl, widgetKey, fetch: fetchImpl, headers: parseHeaders(headersKey) },
      locale,
      labels,
      labelOverrides,
      timeZone,
      t: (key, vars) => formatLabel(labels[key], vars),
    };
  }, [
    api,
    apiBaseUrl,
    widgetKey,
    fetchImpl,
    headersKey,
    locale,
    timeZone,
    parentOverrides,
    ownOverrides,
  ]);

  return <ZeitwahlContext.Provider value={value}>{props.children}</ZeitwahlContext.Provider>;
}

export function useZeitwahl(): ZeitwahlContextValue {
  const value = useContext(ZeitwahlContext);
  if (!value) {
    throw new Error(
      'Zeitwahl: wrap this component in <ZeitwahlProvider apiBaseUrl="…"> or pass apiBaseUrl to <BookingFlow> / <ManageBooking>.'
    );
  }
  return value;
}

export function useOptionalZeitwahl(): ZeitwahlContextValue | null {
  return useContext(ZeitwahlContext);
}
